/**
 * VenueMapDrawer Component
 * Displays a venue's static map image by fetching data from VividSeats API
 * Shows the venue's seating chart/map in a content area (not a drawer itself)
 *
 * BACKEND REQUIREMENT:
 * This component requires a backend proxy endpoint 'GetVenueMap' to be implemented
 * in the DataProvider to handle VividSeats API calls and avoid CORS restrictions.
 * The endpoint should accept venueId parameter and return venue map data.
 */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    SelectChangeEvent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useDataProvider, useNotify } from 'react-admin';
import { VenueMapDrawerProps, VenueMapData, VenueMapResponse, ApiErrorResponse } from '../types';

/**
 * VenueMapDrawer component that fetches and displays venue maps from VividSeats
 * @param {VenueMapDrawerProps} props - Component props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Function} props.onClose - Callback function when drawer is closed
 * @param {RTPVenue | null} props.venue - Selected venue object with id and other venue details
 * @returns {JSX.Element | null} Content component with venue map display
 */
const VenueMapDrawer: React.FC<VenueMapDrawerProps> = ({ open, onClose, venue }) => {
    const [mapData, setMapData] = useState<VenueMapData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [layoutType, setLayoutType] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    /**
     * Handles and formats error messages with comprehensive type checking
     * @param {ApiErrorResponse} err - The error response from API or other sources
     * @returns {string} Formatted error message
     */
    const handleApiError = (err: ApiErrorResponse): string => {
        if (typeof err === 'string') {
            return err;
        }
        
        if (err instanceof Error) {
            return err.message;
        }
        
        if (err && typeof err === 'object' && 'message' in err) {
            return (err as any).message;
        }
        
        return 'Unknown error occurred';
    };

    /**
     * Fetches venue map data from VividSeats API
     * Uses the venue ID to fetch production data and extract static map image
     * @param {number} venueId - The venue ID to fetch map data for
     */
    const fetchVenueMap = async (venueId: number): Promise<void> => {
        if (!venueId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            // Use backend proxy to fetch VividSeats API data to avoid CORS issues
            // The API endpoint follows the pattern: /RTP_Venues/GetVenueMap
            const response = await dataProvider.fetchData('RTP_Venues', `GetVenueMap?venueId=${venueId}`);
            
            // Extract map image URL from the backend response
            // Backend will handle VividSeats API call and return processed data
            if (response && response.data && (response.data as VenueMapResponse).venue && (response.data as VenueMapResponse).venue.static_map_image_url) {
                const data = response.data as VenueMapResponse;
                setMapData({
                    imageUrl: data.venue.static_map_image_url,
                    venueName: data.venue.name || venue?.name || '',
                    venueId: venueId
                });
            } else {
                throw new Error('No map data available from backend proxy');
            }
        } catch (err) {
            console.error('Error fetching venue map:', err);
            
            const errorMessage = handleApiError(err);
            
            // Check if the error is due to missing backend endpoint
            if (errorMessage.includes('404') || errorMessage.includes('Not Found') || errorMessage.includes('GetVenueMap')) {
                setError('Backend proxy endpoint \'GetVenueMap\' is not yet implemented. Please contact your system administrator to enable venue map functionality.');
            } else {
                setError(`Failed to load venue map: ${errorMessage}`);
            }
        } finally {
            setLoading(false);
        }
    };

    /**
     * Effect hook to fetch map data when venue changes or drawer opens
     * Also initializes layout_type from venue data
     */
    useEffect(() => {
        if (open && venue && venue.id) {
            fetchVenueMap(venue.id);
            // Initialize layout_type from venue data, default to empty string if not set
            setLayoutType(venue.layout_type || '');
        }
    }, [open, venue?.id, venue?.layout_type]);

    /**
     * Handles component close and resets state
     */
    const handleClose = (): void => {
        onClose();
        setMapData(null);
        setError(null);
        setLoading(false);
        setLayoutType('');
    };

    /**
     * Handles layout type change
     * @param {SelectChangeEvent} event - The select change event
     */
    const handleLayoutTypeChange = (event: SelectChangeEvent): void => {
        setLayoutType(event.target.value);
    };

    /**
     * Handles saving layout type to the backend
     * Calls the UpdateVenues API endpoint with the updated venue data
     */
    const handleSave = async (): Promise<void> => {
        if (!venue) return;

        setSaving(true);
        try {
            // Call UpdateVenues API with the updated venue data
            const response = await dataProvider.postData('RTP_Venues', 'UpdateVenues', [{
                ...venue,
                layout_type: layoutType || null
            }]);

            if (response?.data && response.data?.success) {
                notify('Layout type updated successfully', { type: 'success' });
                // Update the venue object with the new layout_type
                if (venue) {
                    venue.layout_type = layoutType;
                    handleClose();
                }
            } else {
                notify(`Error: ${response?.body || response?.message || 'Failed to update layout type'}`, { type: 'error' });
            }
        } catch (error) {
            console.error('Error saving layout type:', error);
            notify(`Error: ${error instanceof Error ? error.message : 'Failed to update layout type'}`, { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handles image load error
     * @param {React.SyntheticEvent<HTMLImageElement>} _e - Image error event
     */
    const handleImageError = (_e: React.SyntheticEvent<HTMLImageElement>): void => {
        if (mapData) {
            console.error('Map image failed to load:', mapData.imageUrl);
            setError('Failed to load venue map image');
        }
    };

    /**
     * Handles successful image load
     */
    const handleImageLoad = (): void => {
        if (mapData) {
            console.log('Map image loaded successfully:', mapData.imageUrl);
        }
    };

    if (!open || !venue) {
        return null;
    }

    return (
        <Box sx={{ height: '100%', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2">
                    Venue Map
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    <strong>{venue.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    ID: {venue.id} | {venue.city}, {venue.state}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Verification: {venue.verification_status === 1 ? 'Whitelisted' : venue.verification_status === 2 ? 'Blacklisted' : 'Not Verified'}
                </Typography>
            </Box>

            {/* Layout Type Selection */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Venue Layout Configuration
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="layout-type-label">Layout Type</InputLabel>
                        <Select
                            labelId="layout-type-label"
                            id="layout-type-select"
                            value={layoutType}
                            label="Layout Type"
                            onChange={handleLayoutTypeChange}
                            disabled={saving}
                        >
                            <MenuItem value="">
                                <em>Not Set</em>
                            </MenuItem>
                            <MenuItem value="standard">Standard</MenuItem>
                            <MenuItem value="horseshoe">Horseshoe</MenuItem>
                            <MenuItem value="table">Table</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || layoutType === (venue.layout_type || '')}
                        sx={{ minWidth: 100 }}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Configure the venue's seating layout type to override automatic classification in analysis.
                </Typography>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {loading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Loading venue map...
                        </Typography>
                    </Box>
                )}
                
                {error && (
                    <Alert severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                )}
                
                {mapData && !loading && !error && (
                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {mapData.venueName}
                        </Typography>
                        <img
                            src={mapData.imageUrl}
                            alt={`Map of ${mapData.venueName}`}
                            style={{
                                maxWidth: '100%',
                                width: '100%',
                                height: 'auto',
                                border: '1px solid #ddd',
                                borderRadius: 4
                            }}
                            onError={handleImageError}
                            onLoad={handleImageLoad}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default VenueMapDrawer;